import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchPDPInitialLoad,
  fetchInvestCalLoad,
  fetchPDPBidCenterData,
  fetchPDPLatestBidCenterData,
  setIsNDAAccepted,
  setIsCifCompletedForPACT,
  resetPDPState,
} from '@/redux/PDP/pdpSlice';
import { setProfileInfo } from '@/redux/user/userSlice';
import { getUserProfile } from '@/api/myProfile';
import {
  getPriceHistory,
  getTax,
  getCMS,
  getDisclaimer,
  getEMD,
  getInspectionContingency,
  getAds,
  getSmsWatchData,
  getNearbyProperties,
  updatePropertyViewCount,
} from '@/api/PDP';
import { isGetCodeAllowed } from '@/api/AccessCode';
import { trackApiCall } from '@/utils/analytics';

export const usePDPData = (propertyId, pdpInitData, isUserLoggedIn) => {

  const dispatch = useDispatch();
    const pdpData = useSelector((state) => state.pdp.pdpInitData);


  // State management with preservation
  const [cmsData, setCmsData] = useState([]);
  const [cmsLoaded, setCmsLoaded] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showGetCode, setShowGetCode] = useState(true);
  const [taxRecord, setTaxRecord] = useState();
  const [priceHistory, setPriceHistory] = useState([]);
  const [cmsKeyInfographic, setCmsKeyInfographic] = useState();
  const [disclaimer, setDisclaimer] = useState();
  const [emd, setEmd] = useState();
  const [pdpAds, setPDPAds] = useState();
  const [nearbyProperties, setNearbyProperties] = useState([]);
  const [propertyWatchStatus, setPropertyWatchStatus] = useState(false);
  const [inspectionContigencyDesc, setInspectionContigencyDesc] = useState();
  const [isLoadingAdditionalData, setIsLoadingAdditionalData] = useState(false);
  const loadedPropertyId = useRef(null);

  // Load user profile when logged in
  // const loadUserProfile = useCallback(async () => {
  //   if (!isUserLoggedIn) return;

  //   try {
  //     const response = await getUserProfile();
  //     dispatch(setProfileInfo(response?.data));
  //   } catch (error) {
  //   }
  // }, [isUserLoggedIn, dispatch]);

  // Load CMS data

  const loadCMSData = useCallback(async () => {
    if (cmsLoaded && !isInitialLoad) return;

    try {
      const response = await getCMS("pages/pdp");
      const sections = response?.results?.[0]?.sections;
      setCmsData(sections);
      setCmsKeyInfographic(sections?.pdpKeyInfographic);
      setCmsLoaded(true);
      setIsInitialLoad(false);
    } catch (error) {
      setCmsLoaded(true);
      setIsInitialLoad(false);
    }
  }, [cmsLoaded, isInitialLoad]);

  // Load additional API data
  const loadAdditionalData = useCallback(async () => {
    if (!pdpInitData?.propertyDetails?.propertyId || isLoadingAdditionalData) return;
    if (loadedPropertyId.current === pdpInitData.propertyDetails.propertyId) return;

    setIsLoadingAdditionalData(true);
    loadedPropertyId.current = pdpInitData.propertyDetails.propertyId;

    const listingId = pdpInitData?.propertyDetails?.listingId;
    const sellerId = pdpInitData.propertyDetails.sellerId;
    const propCategory = pdpInitData.propertyDetails.commonAttributes.propCategory;
    const occupancy = pdpInitData.propertyDetails.commonAttributes.isOccupied;
    const listingType = pdpInitData.listingDetail?.listingType;
    const status = pdpInitData.propertyDetails.commonAttributes.status;



    // Update view count
    updatePropertyViewCount(propertyId, listingId);

    // Set NDA acceptance
    dispatch(setIsNDAAccepted(pdpInitData?.ndaCommercialAccepted));
    dispatch(setIsCifCompletedForPACT(pdpInitData?.cifCompleted));


    // Load SMS watch status
    try {
      const smsResponse = await getSmsWatchData(propertyId);
      setPropertyWatchStatus(smsResponse?.data?.data === "Y");
    } catch (error) {
    }

    // Dispatch Redux actions
    if (propCategory !== 'COMMERCIAL') {
      dispatch(fetchInvestCalLoad(propertyId));
    }
    // Note: BidCenter calls moved to main component to prevent duplicates

    // Batch API calls
    const apiCalls = [
      getInspectionContingency(sellerId, propCategory, occupancy, listingType, propertyId).then(response => setInspectionContigencyDesc(response?.data)),
      getPriceHistory(propertyId).then(response => setPriceHistory(response || [])),
      getTax(propertyId).then(response => {
        setTaxRecord(response);
        trackApiCall({
          eventName: "property_tax_loaded",
          eventCategory: "NEW-PDP",
          eventLabel: `PropertyId: ${propertyId}`
        })
      }).catch(error => {
        setTaxRecord(null);
        trackApiCall({
          eventName: "property_tax_load_error",
          eventCategory: "NEW-PDP",
          eventLabel: `PropertyId: ${propertyId}`
        })
      }),
      getDisclaimer(propertyId).then(response => setDisclaimer(response?.data)),
      getEMD(pdpData.propertyDetails?.listingId).then(response => setEmd(response?.data)),
      getAds(propertyId).then(response => setPDPAds(response?.data)),
      getNearbyProperties(propertyId).then(response => setNearbyProperties(response || []))
    ];

    // Handle conditional API calls
    if (status === 'SPND') {
      apiCalls.push(
        isGetCodeAllowed(listingId)
          .then(response => setShowGetCode(response?.data === true))
      );
    }

    // Execute all API calls concurrently
    await Promise.allSettled(apiCalls);
    setIsLoadingAdditionalData(false);
  }, [pdpData?.propertyDetails?.propertyId]);

  // Reset state when propertyId changes
  useEffect(() => {
    if (!propertyId) return;
    dispatch(resetPDPState());
  }, [propertyId]);

  // Initial load effect
  useEffect(() => {
    if (!propertyId) return;

    dispatch(fetchPDPInitialLoad(propertyId));
    loadCMSData();
  }, [propertyId]);

  // Separate effect for user profile to avoid resetting data
  // useEffect(() => {
  //   loadUserProfile();
  // }, [isUserLoggedIn, loadUserProfile]);

  // Load additional data when PDP init data is available
  useEffect(() => {
    if (pdpInitData?.propertyDetails?.propertyId !== propertyId) return;

    if (pdpInitData?.propertyDetails?.propertyId) {
      window.scrollTo(0, 0);
      loadAdditionalData();
    }
  }, [pdpData?.propertyDetails?.propertyId]);

  return {
    cmsData,
    cmsLoaded,
    showGetCode,
    taxRecord,
    priceHistory,
    cmsKeyInfographic,
    disclaimer,
    emd,
    pdpAds,
    nearbyProperties,
    propertyWatchStatus,
    inspectionContigencyDesc,
    setShowGetCode,
    setPropertyWatchStatus,
  };
};